import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import "./Membership.css";

function Membership() {
  const { token } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState("yearly");
  const [toastMessage, setToastMessage] = useState({ type: "", text: "" });
  // Helper function to show toast messages
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: "", text: "" }), duration);
  };
  const fetchMembershipData = useCallback(async () => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://192.168.68.67:5000"
        }/api/auth/membership`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();
      setMembershipData(data);
    } catch (error) {
      console.error("Error fetching membership:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchMembershipData();
  }, [fetchMembershipData]);

  if (loading) {
    return (
      <div className="membership-container">
        <div className="loading-state">Loading membership information...</div>
      </div>
    );
  }

  const currentPlan = membershipData?.subscriptionPlan || "free";
  const isFreePlan = currentPlan === "free";

  const handleUpgrade = async (plan, billingCycle) => {
    if (window.gtag)
      window.gtag("event", "checkout_started", {
        plan,
        billing_cycle: billingCycle,
      });
    setUpdating(true);
    try {
      // Map plan and billing cycle to Stripe price IDs
      const priceIds = {
        "pro-monthly": "price_1SemxxBXYeChKN7K2qxEO4bv",
        "pro-yearly": "price_1Sen1rBXYeChKN7Kb1fKf7iY",
        "premium-monthly": "price_1Sen34BXYeChKN7KRa6U2lGK",
        "premium-yearly": "price_1Sn2EHBXYeChKN7KXVJbYvsW",
        "organization-monthly": "price_1Sk5pkBXYeChKN7Km5EIeSnI",
        "organization-yearly": "price_1Sk5qqBXYeChKN7KUeMcu5Z3",
      };

      const priceId = priceIds[`${plan}-${billingCycle}`];

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://192.168.68.67:5000"
        }/api/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ priceId, plan }),
        },
      );

      const data = await response.json();
      // Check if subscription was updated directly (upgrade) or needs checkout (new subscription)
      if (data.updated) {
        // Already showing spinner from top of function
        showToast("success", "Subscription updated successfully!");

        // Wait a moment for webhook to process, then refresh data
        setTimeout(async () => {
          await fetchMembershipData();
          setUpdating(false);
        }, 1500);
      } else if (data.url) {
        // Redirect to Stripe Checkout for new subscription
        window.location.href = data.url;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Upgrade error:", error);
      showToast("error", "Failed to start checkout. Please try again.");
      setUpdating(false);
    } finally {
      // Don't setUpdating(false) here because we want it to stay on during redirect
      // It will be cleared by the setTimeout or page navigation
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://192.168.68.67:5000"
        }/api/stripe/create-portal-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        console.error("Portal error:", error);
        showToast(
          "error",
          "No active subscription found. Please upgrade to a paid plan first.",
        );
        return;
      }

      const { url } = await response.json();
      window.open(url, "_blank");
    } catch (error) {
      console.error("Portal error:", error);
      showToast("error", "Failed to open subscription portal.");
    }
  };

  return (
    <>
      <Navbar />

      <div className="membership-container">
        <div className="membership-content">
          <div className="membership-header">
            <div className="header-content">
              <h1>Membership & Billing</h1>
              <p>Manage your subscription and upgrade your plan</p>
            </div>

            <div className="current-plan-indicator">
              <div className="plan-row">
                <span className="indicator-label">Your Plan:</span>
                <span className={`plan-badge-large plan-badge-${currentPlan}`}>
                  {currentPlan.toUpperCase()}
                  {!isFreePlan && membershipData?.billingCycle && (
                    <span className="plan-interval">
                      {" "}
                      {membershipData.billingCycle === "monthly"
                        ? "MONTHLY"
                        : "YEARLY"}
                    </span>
                  )}
                </span>
              </div>
              {!isFreePlan && (
                <button
                  className="billing-details-link"
                  onClick={handleManageSubscription}
                >
                  View billing details & payment history →
                </button>
              )}
            </div>
          </div>
          {/* Proration Notice */}
          <div className="proration-notice">
            <div className="notice-icon">ℹ️</div>
            <div className="notice-content">
              <strong>How billing works:</strong> When you upgrade or downgrade
              between tiers, we don't issue refunds. Instead, Stripe
              automatically calculates proration credits or charges based on
              your remaining subscription time. Upgrades are charged immediately
              with credit for your unused time. Downgrades apply credit toward
              your next billing cycle.
            </div>
          </div>
          <div className="upgrade-section">
            <h2>Compare Plans</h2>
            <p className="upgrade-subtitle">
              Choose the plan that's right for you
            </p>

            {/* Billing Cycle Toggle */}
            <div className="billing-cycle-toggle">
              <button
                className={`cycle-btn ${
                  selectedBillingCycle === "monthly" ? "active" : ""
                }`}
                onClick={() => setSelectedBillingCycle("monthly")}
              >
                Monthly
              </button>
              <button
                className={`cycle-btn ${
                  selectedBillingCycle === "yearly" ? "active" : ""
                }`}
                onClick={() => setSelectedBillingCycle("yearly")}
              >
                Yearly
                <span className="save-badge-toggle">Save up to $80</span>
              </button>
            </div>

            {/* Comparison Matrix */}
            <div className="comparison-wrapper">
              <div className="comparison-table">
                {/* Header Row */}
                <div className="comparison-row header-row">
                  <div className="comparison-cell feature-label"></div>

                  {/* Free Plan Header */}
                  <div className="comparison-cell plan-header">
                    <div className="plan-name">Free</div>
                    <div className="plan-price">$0</div>
                    <div className="plan-period">forever</div>
                    {currentPlan === "free" && (
                      <div className="current-plan-badge">Current Plan</div>
                    )}
                  </div>

                  {/* Pro Plan Header */}
                  <div className="comparison-cell plan-header popular-plan">
                    <div className="popular-flag">Most Popular</div>
                    <div className="plan-name">Pro</div>
                    <div className="plan-price">
                      {selectedBillingCycle === "monthly" ? "$7.99" : "$6.58"}
                    </div>
                    <div className="plan-period">
                      {selectedBillingCycle === "monthly"
                        ? "/month"
                        : "/month (billed yearly)"}
                    </div>
                    {currentPlan === "pro" &&
                      membershipData?.billingCycle === selectedBillingCycle && (
                        <div className="current-plan-badge">Current Plan</div>
                      )}
                  </div>

                  {/* Premium Plan Header */}
                  <div className="comparison-cell plan-header">
                    <div className="plan-name">Premium</div>
                    <div className="plan-price">
                      {selectedBillingCycle === "monthly" ? "$15.99" : "$13.25"}
                    </div>
                    <div className="plan-period">
                      {selectedBillingCycle === "monthly"
                        ? "/month"
                        : "/month (billed yearly)"}
                    </div>
                    {currentPlan === "premium" &&
                      membershipData?.billingCycle === selectedBillingCycle && (
                        <div className="current-plan-badge">Current Plan</div>
                      )}
                  </div>

                  {/* Organization Plan Header */}
                  <div className="comparison-cell plan-header org-plan">
                    <div className="org-flag">For Leagues</div>
                    <div className="plan-name">Organization</div>
                    {currentPlan === "organization" &&
                      membershipData?.billingCycle === selectedBillingCycle && (
                        <div className="current-plan-badge">Current Plan</div>
                      )}
                    <div className="plan-price">
                      {selectedBillingCycle === "monthly" ? "$39.99" : "$33.25"}
                    </div>
                    <div className="plan-period">
                      {selectedBillingCycle === "monthly"
                        ? "/month"
                        : "/month (billed yearly)"}
                    </div>
                  </div>
                </div>

                {/* ── GRAPHICS SECTION ── */}
                <div className="comparison-row section-group-header">
                  <div className="comparison-cell feature-label">Graphics</div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">Teams</div>
                  <div className="comparison-cell">1</div>
                  <div className="comparison-cell">5</div>
                  <div className="comparison-cell">Unlimited</div>
                  <div className="comparison-cell">Unlimited</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">Players</div>
                  <div className="comparison-cell">Unlimited</div>
                  <div className="comparison-cell">Unlimited</div>
                  <div className="comparison-cell">Unlimited</div>
                  <div className="comparison-cell">Unlimited</div>
                </div>
                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Saved Graphics
                  </div>
                  <div className="comparison-cell">10</div>
                  <div className="comparison-cell">50</div>
                  <div className="comparison-cell">Unlimited</div>
                  <div className="comparison-cell">Unlimited</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    All Templates & Designs
                  </div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Custom Team Colors
                  </div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Logo Upload
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Watermark Removed
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Background Removal & Cutouts
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Roster Management
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Animated Graphics (GIF)
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell coming-soon-cell">
                    Coming Soon
                  </div>
                  <div className="comparison-cell coming-soon-cell">
                    Coming Soon
                  </div>
                  <div className="comparison-cell coming-soon-cell">
                    Coming Soon
                  </div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Video Highlights w/ Stats
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell coming-soon-cell">
                    Coming Soon
                  </div>
                  <div className="comparison-cell coming-soon-cell">
                    Coming Soon
                  </div>
                </div>

                {/* ── ANALYTICS SECTION ── */}
                <div className="comparison-row section-group-header">
                  <div className="comparison-cell feature-label">
                    Player Analytics
                  </div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Stat Tracking & Game History
                  </div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Brag Cards
                  </div>
                  <div className="comparison-cell">10</div>
                  <div className="comparison-cell">50</div>
                  <div className="comparison-cell">Unlimited</div>
                  <div className="comparison-cell">Unlimited</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Benchmark Comparisons by Level
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Development Insights
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Ask the Analyst
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">20 messages/mo</div>
                  <div className="comparison-cell">200 messages/mo</div>
                  <div className="comparison-cell">200 messages/mo</div>
                </div>

                {/* ── ORGANIZATION SECTION ── */}
                <div className="comparison-row section-group-header">
                  <div className="comparison-cell feature-label">
                    Organization
                  </div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                  <div className="comparison-cell"></div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Organization Team Management
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Invite Coaches to Teams
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Bulk Team CSV Import
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Control Template & Feature Restrictions
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                </div>

                <div className="comparison-row">
                  <div className="comparison-cell feature-label">
                    Control Team Logo & Color Defaults
                  </div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✗</div>
                  <div className="comparison-cell">✓</div>
                </div>

                {/* CTA Row */}
                <div className="comparison-row cta-row">
                  <div className="comparison-cell feature-label"></div>
                  <div className="comparison-cell">
                    {currentPlan === "free" ? (
                      <button className="btn-current" disabled>
                        Current Plan
                      </button>
                    ) : (
                      <div className="downgrade-info">
                        <p className="no-refund-text">
                          We don't offer refunds. Need help?{" "}
                          <a href="/contact" className="contact-link">
                            Contact us
                          </a>
                        </p>
                        <p className="cancel-note">
                          To cancel a current subscription, use the{" "}
                          <button
                            className="link-button-inline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManageSubscription();
                            }}
                          >
                            Stripe Portal
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="comparison-cell">
                    {currentPlan === "pro" &&
                    membershipData?.billingCycle === selectedBillingCycle ? (
                      <button className="btn-current" disabled>
                        Current Plan
                      </button>
                    ) : (
                      <button
                        className="btn-upgrade-plan"
                        onClick={() => {
                          setPendingUpgrade({
                            plan: "pro",
                            billingCycle: selectedBillingCycle,
                          });
                          setShowConfirmModal(true);
                        }}
                      >
                        {currentPlan === "pro"
                          ? "Switch to " +
                            (selectedBillingCycle === "monthly"
                              ? "Monthly"
                              : "Yearly")
                          : "Upgrade to Pro"}
                      </button>
                    )}
                  </div>
                  <div className="comparison-cell">
                    {currentPlan === "premium" &&
                    membershipData?.billingCycle === selectedBillingCycle ? (
                      <button className="btn-current" disabled>
                        Current Plan
                      </button>
                    ) : (
                      <button
                        className="btn-upgrade-plan"
                        onClick={() => {
                          setPendingUpgrade({
                            plan: "premium",
                            billingCycle: selectedBillingCycle,
                          });
                          setShowConfirmModal(true);
                        }}
                      >
                        {currentPlan === "premium"
                          ? "Switch to " +
                            (selectedBillingCycle === "monthly"
                              ? "Monthly"
                              : "Yearly")
                          : "Upgrade to Premium"}
                      </button>
                    )}
                  </div>
                  <div className="comparison-cell">
                    {currentPlan === "organization" &&
                    membershipData?.billingCycle === selectedBillingCycle ? (
                      <button className="btn-current" disabled>
                        Current Plan
                      </button>
                    ) : (
                      <button
                        className="btn-upgrade-plan org-btn"
                        onClick={() => {
                          setPendingUpgrade({
                            plan: "organization",
                            billingCycle: selectedBillingCycle,
                          });
                          setShowConfirmModal(true);
                        }}
                      >
                        {currentPlan === "organization"
                          ? "Switch to " +
                            (selectedBillingCycle === "monthly"
                              ? "Monthly"
                              : "Yearly")
                          : "Upgrade to Organization"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Premium User - Manage Subscription */}
          {!isFreePlan && membershipData?.subscriptionStatus === "active" && (
            <div className="manage-subscription-section">
              <h2>Manage Subscription</h2>
              <p>
                Update your plan, payment method, or view billing history in the
                Stripe Customer Portal.
              </p>
              <div className="manage-actions">
                <button
                  className="btn btn-primary"
                  style={{ width: "auto", flexShrink: 0 }}
                  onClick={handleManageSubscription}
                >
                  Open Subscription Portal →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Toast Notifications */}
      {toastMessage.text && toastMessage.type === "success" && (
        <div className="toast-message toast-success">{toastMessage.text}</div>
      )}
      {toastMessage.text && toastMessage.type === "error" && (
        <div className="toast-message toast-error">{toastMessage.text}</div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && pendingUpgrade && (
        <div
          className="modal-overlay"
          onClick={() => setShowConfirmModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Subscription Change</h3>
            <p>
              You're about to change your subscription to{" "}
              <strong>
                {pendingUpgrade.plan.charAt(0).toUpperCase() +
                  pendingUpgrade.plan.slice(1)}{" "}
                (
                {pendingUpgrade.billingCycle === "monthly"
                  ? "Monthly"
                  : "Yearly"}
                )
              </strong>
              .
              {currentPlan !== "free" && (
                <>
                  <br />
                  <br />
                  Stripe will automatically calculate any proration - you'll
                  either be charged the difference for an upgrade or receive
                  credit for a downgrade.
                </>
              )}
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingUpgrade(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={() => {
                  setShowConfirmModal(false);
                  handleUpgrade(
                    pendingUpgrade.plan,
                    pendingUpgrade.billingCycle,
                  );
                }}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Updating Overlay */}
      {updating && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="updating-overlay-content">
            <div className="spinner"></div>
            <h3>Updating Subscription...</h3>
            <p>Please wait while we process your request</p>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default Membership;
